import { Component, Input, OnInit, ViewChild, forwardRef, Output, EventEmitter, ChangeDetectorRef, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { ChangeEventArgs, RichTextEditorComponent } from '@syncfusion/ej2-angular-richtexteditor';
import { MentionComponent } from '@syncfusion/ej2-angular-dropdowns';
import { Query } from '@syncfusion/ej2-data';
import { ConfirmService } from 'src/app/libs/confirm/confirm.service';
import { Cid10Service } from 'src/app/servicos/cid10.service';
import { Cid10Dto } from 'src/app/dtos/doencas/cid10-dto';
import { debounceTime, distinctUntilChanged, Observable, Subject, switchMap, of, map } from 'rxjs';
import { HistoriaModeloPesquisaComponent } from 'src/app/pesquisa/historia-modelo-pesquisa/historia-modelo-pesquisa.component';
import { HistoriaModeloService } from 'src/app/servicos/historia-modelo.service';
import { LHistoriaModeloDto } from 'src/app/dtos/modelos/historia-modelo-dto';
import { ToastService } from 'src/app/libs/toast/toast.service';

interface MentionItem {
  codigo: string;
  descricao: string;
  codigoDescricao: string;
  display: string;
}

@Component({
  selector: 'app-texto-editor-cid',
  templateUrl: './texto-editor-cid.component.html',
  styleUrls: ['./texto-editor-cid.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextoEditorCidComponent),
      multi: true
    }
  ]
})
export class TextoEditorCidComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  icon: string = '\"fas fa-file-medical\"';

  @ViewChild('rteObj', { static: false })
  rteObj!: RichTextEditorComponent;

  @ViewChild('mentionObj', { static: false })
  mentionObj!: MentionComponent;

  @ViewChild('historiaPesquisa', { static: false })
  historiaPesquisa!: HistoriaModeloPesquisaComponent;

  @Input()
  label: string = '';

  @Input()
  placeholder: string = '';

  public tools: object = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
      'ClearFormat', 'Print', 'SourceCode', '|', 'FullScreen', '|',
      {
        tooltipText: 'Carregar Modelo de História Clínica',
        id: 'loadModel',
        prefixIcon: 'e-icons fa fas fa-file-medical'
      },
      '|',
      {
        tooltipText: 'Carregar Último Texto',
        id: 'update',
        prefixIcon: 'e-icons fa fas fa-sync'
      }, 'Undo', 'Redo'
    ]
  };

  @Input()
  textoResete: string | null = null;

  @Input()
  height: string = '320px';

  @Input()
  formControl: FormControl | null = null;

  @Input()
  formControlName: string | null = null;

  value: string | null = null;

  @Output()
  cidAdicionado = new EventEmitter<{ codigo: string; descricao: string }>();

  @Output()
  cidRemovido = new EventEmitter<{ codigo: string; descricao: string }>();

  @Output()
  textoResetado = new EventEmitter<void>();

  private cidsNoTexto: Map<string, { codigo: string; descricao: string }> = new Map();
  private editableElement: HTMLElement | null = null;
  private keyupListener: ((e: Event) => void) | null = null;
  private cutListener: ((e: Event) => void) | null = null;
  private pasteListener: ((e: Event) => void) | null = null;

  // Mention configuration
  public mentionChar: string = '@';
  public mentionData: MentionItem[] = [];
  public fields: object = { text: 'codigoDescricao', value: 'codigo' };
  public target: string = '';
  public popupWidth: string = '400px';
  public popupHeight: string = '200px';
  
  private searchSubject = new Subject<{ text: string; event: any }>();
  public minLength: number = 2;

  constructor(
    private confirm: ConfirmService,
    private cid10Service: Cid10Service,
    private cdr: ChangeDetectorRef,
    private historiaModeloService: HistoriaModeloService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    // Configurar busca com debounce
    this.searchSubject.pipe(
      switchMap(() => this.cid10Service.listarCids10())
    ).subscribe((result: Cid10Dto[] ) => {
        console.log('Subject CIDs encontrados:', result);

        this.mentionData = result.map(cid => ({
            codigo: cid.codigo,
            descricao: cid.descricao,
            codigoDescricao: `${cid.codigo} - ${cid.descricao}`,
            display: `${cid.codigo} - ${cid.descricao}`
        }));
    });

    this.searchSubject.next({ text: '', event: {} });
  }

  ngAfterViewInit(): void {
    // Configurar o target do mention após a view ser inicializada
    if (this.rteObj) {
      this.target = '#rteObj_rte-edit-view';
      this.cdr.detectChanges();
      
      // Adicionar listeners de teclado para detectar remoções
      setTimeout(() => {
        const editPanel = this.rteObj?.contentModule?.getEditPanel?.();
        this.editableElement = editPanel ? editPanel as HTMLElement : null;
        
        if (this.editableElement) {
          // Listener para todas as teclas - detectar remoções sempre
          this.keyupListener = (e: Event) => {
            setTimeout(() => {
              const currentContent = this.editableElement?.innerHTML || '';
              this.detectarCidsRemovidos(currentContent);
            }, 50);
          };
          
          this.editableElement.addEventListener('keyup', this.keyupListener);
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Remover event listeners para evitar memory leaks
    if (this.editableElement) {
      if (this.keyupListener) {
        this.editableElement.removeEventListener('keyup', this.keyupListener);
      }
      if (this.cutListener) {
        this.editableElement.removeEventListener('cut', this.cutListener);
      }
      if (this.pasteListener) {
        this.editableElement.removeEventListener('paste', this.pasteListener);
      }
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {

  }

  change(args: any) {
    this.onChange(args.value);
  }

  private detectarCidsNoTexto(html: string | null): void {
    if (!html) {
      return;
    }

    // Criar um elemento temporário para fazer parsing do HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Buscar todos os spans com classe 'cid-codigo'
    const codigoSpans = tempDiv.querySelectorAll('span.cid-codigo');
    
    codigoSpans.forEach(span => {
      const codigo = span.textContent?.trim();
      // Buscar o span de descrição adjacente
      const descricaoSpan = span.nextElementSibling as HTMLElement;
      const descricao = descricaoSpan?.textContent?.trim() || '';
      
      if (codigo) {
        this.cidsNoTexto.set(codigo, { codigo, descricao });
      }
    });
  }

  private detectarCidsRemovidos(texto: string | null): void {
    if (!texto) {
      // Se não há texto, todos os CIDs foram removidos
      this.cidsNoTexto.forEach(cid => {
        this.cidRemovido.emit(cid);
      });
      this.cidsNoTexto.clear();
      return;
    }

    // Criar um elemento temporário para fazer parsing do HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = texto;

    // Buscar todos os códigos CID presentes no texto atual
    const codigoSpans = tempDiv.querySelectorAll('span.cid-codigo');
    const cidsAtuais = new Map<string, { codigo: string; descricao: string }>();
    
    codigoSpans.forEach(span => {
      const codigo = span.textContent?.trim();
      const descricaoSpan = span.nextElementSibling as HTMLElement;
      const descricao = descricaoSpan?.textContent?.trim() || '';
      
      if (codigo) {
        cidsAtuais.set(codigo, { codigo, descricao });
      }
    });

    // Detectar CIDs removidos
    this.cidsNoTexto.forEach((cid, codigo) => {
      if (!cidsAtuais.has(codigo)) {
        this.cidRemovido.emit(cid);
      }
    });

    // Atualizar lista de CIDs no texto
    this.cidsNoTexto = cidsAtuais;
  }

  toolbarClick(args: ClickEventArgs) {
    if (args.item.id === 'update') {
      this.confirm.confirm('Deseja realmente carregar o último texto? O conteúdo atual será substituído.').subscribe((val) => {
        if (val) {
          this.textoResetado.emit();
        }
      });
    } else if (args.item.id === 'loadModel') {
      this.abrirModalHistoriaModelo();
    }
  }

  // Evento quando um item é selecionado
  onItemSelected(args: any): void {
    console.log('CID selecionado:', args.itemData);

    if (args.itemData) {
      const item = args.itemData as MentionItem;
      
      // Adicionar CID ao mapa e emitir evento
      // O HTML será gerado automaticamente pelo displayTemplate
      this.cidsNoTexto.set(item.codigo, { 
        codigo: item.codigo, 
        descricao: item.descricao 
      });
      
      this.cidAdicionado.emit({ 
        codigo: item.codigo, 
        descricao: item.descricao 
      });
    }
  }

  onMentionChange(args: any): void {
    console.log('mention change:', args);
    // Detectar novos CIDs após qualquer mudança
    setTimeout(() => {
        if(args.itemData)
            this.detectarNovosCidsAdicionados(args.itemData);
    }, 100);
  }

  private detectarNovosCidsAdicionados(data?: { codigo: string, descricao: string }): void {
    if(data) {
        if (!this.cidsNoTexto.has(data.codigo)) {
            // Novo CID detectado - adicionar e emitir evento
            this.cidsNoTexto.set(data.codigo, { codigo: data.codigo, descricao: data.descricao });
            this.cidAdicionado.emit({ codigo: data.codigo, descricao: data.descricao });
        }

        return;
    }

    const currentContent = this.editableElement?.innerHTML || this.rteObj?.value || '';
    if (!currentContent) return;

    // Criar um elemento temporário para fazer parsing do HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentContent;

    // Buscar todos os spans com classe 'cid-codigo'
    const codigoSpans = tempDiv.querySelectorAll('span.cid-codigo');
    
    codigoSpans.forEach(span => {
      const codigo = span.textContent?.trim();
      const descricaoSpan = span.nextElementSibling as HTMLElement;
      const descricao = descricaoSpan?.textContent?.trim() || '';
      
      if (codigo && !this.cidsNoTexto.has(codigo)) {
        // Novo CID detectado - adicionar e emitir evento
        this.cidsNoTexto.set(codigo, { codigo, descricao });
        this.cidAdicionado.emit({ codigo, descricao });
      }
    });
  }

  abrirModalHistoriaModelo(): void {
    if (!this.historiaPesquisa) {
      this.toast.showWarning('Componente de pesquisa não disponível.');
      return;
    }

    this.historiaPesquisa.abrir();
  }

  modeloSelecionado(modelo: LHistoriaModeloDto): void {
    this.confirm.confirm('Deseja realmente carregar este modelo? O conteúdo atual será substituído.').pipe(
      switchMap((confirmado) => {
        if (!confirmado) {
          return of(null);
        }
        return this.historiaModeloService.consultar(modelo.id);
      })
    ).subscribe({
      next: (historiaCompleta) => {
        if (historiaCompleta) {
          // Carregar o conteúdo no editor
          this.value = historiaCompleta.conteudo;
          this.rteObj.value = historiaCompleta.conteudo;
          
          // Detectar CIDs no texto carregado
          this.detectarCidsNoTexto(historiaCompleta.conteudo);
          
          // Emitir evento de mudança
          this.onChange(historiaCompleta.conteudo);
          
          this.toast.showSuccess('Modelo carregado com sucesso.');
          
          // Fechar o modal
          if (this.historiaPesquisa) {
            this.historiaPesquisa.fechar();
          }
        }
      },
      error: (err) => {
        this.toast.showError('Erro ao carregar modelo de história clínica.');
      }
    });
  }

}
